import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { BuildCircle, FiberNew } from '@mui/icons-material'
import { Box, Button, Chip, CircularProgress, Divider, Grid, Stack, Typography } from '@mui/material'

import { AnimatedImage } from '../../../../../../components/AnimatedImage/AnimatedImage'
import { HtmlContentParser } from '../../../../../../components/HtmlContentParser/HtmlContentParser'
import { liveEventReviewModalParserOptions } from '../../../../../../components/LiveEventModalLink/LiveEventModalLink'
import { useDisplayExperimentalFeature, ExperimentalFeature } from '../../../../../../hooks/useDisplayExperimentalFeature'
import { useInternalAccessCheck } from '../../../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../../../account/hooks/useCurrentUserLanguage'
import { MotivationTypeBadge } from '../../../../../motivations/components/MotivationTypeBadge/MotivationTypeBadge'
import { liveEventPriceRange } from '../../../../hooks/liveEventPriceRange'
import { useLiveEventArchetypeName } from '../../../../hooks/liveEventsArchetypes'
import { TrackingEventTimelineItem } from '../../../../types/LiveEvents'
import { EventKPIIndicator } from '../../../EventKPIIndicator/EventKPIIndicator'
import { PriceRangeIndicator } from '../../../PriceRangeIndicator/PriceRangeIndicator'

const defaultTimelineItemBGColor = '#273143'

interface LiveEventInfoTooltipContentProps {
  item: TrackingEventTimelineItem
  itemContext: any
  descriptionRawHtml?: string | null
  bgColor: string
  newItem: boolean
  firstInstance: boolean
  mechanicChange: boolean
  inactive?: boolean
  onViewDetails?: () => void
}

export const LiveEventInfoTooltipContent: FC<LiveEventInfoTooltipContentProps> = ({
  item,
  itemContext,
  descriptionRawHtml,
  bgColor,
  newItem,
  firstInstance,
  mechanicChange,
  inactive,
  onViewDetails,
}) => {
  const { t } = useTranslation()
  const internalUser = useInternalAccessCheck()
  const userLanguage = useCurrentUserLanguage()

  const displayExperimentalFeatureMotivations = useDisplayExperimentalFeature(ExperimentalFeature.LiveEventMotivations) // NOTE: Remove check when we want to release Live Event Motivations
  const priceRange = liveEventPriceRange(item.trackingEvent?.pricePoints, item.trackedGame?.game?.currencies)
  const archetypeName = useLiveEventArchetypeName({ archetypeId: item.trackingEvent?.archetypeEvolutionData?.archetypeId, trackedGame: item.trackedGame })
  const blueprintDescription = item.trackingEvent?.archetypeEvolutionData?.descriptions?.[userLanguage]
    ? item.trackingEvent?.archetypeEvolutionData?.descriptions?.[userLanguage]
    : item.trackingEvent?.archetypeEvolutionData?.descriptions?.['en']

  if (!item.trackingEvent) {
    return (
      <Box>
        <CircularProgress size={24} />
      </Box>
    )
  }

  const showWipMessage = inactive && !internalUser

  return (
    // Cap the whole tooltip at the same 600px the description Grid below already uses. The blueprint
    // "Change in this version" paragraph and its divider sit outside that Grid, so once their own
    // width cap was removed (to kill the right-side gap) they had no bound and stretched the entire
    // tooltip almost full-screen. Bounding the root keeps that paragraph full-width (no gap) while
    // stopping the tooltip from ballooning. (GR-2957)
    <Box sx={{ fontSize: '14px', maxWidth: 600 }}>
      <Stack mb={0.5} direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          {newItem && <FiberNew sx={{ position: 'relative', fontSize: '20px', verticalAlign: 'middle', top: '-1px', mr: 0.5 }} />}
          {mechanicChange && internalUser && (
            <BuildCircle sx={{ position: 'relative', fontSize: '20px', verticalAlign: 'middle', top: '-2px', mr: 0.5, color: 'info.main' }} />
          )}
          <strong style={{ fontSize: '18px' }}>{itemContext.title}</strong>
          {inactive && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ verticalAlign: 'middle', ml: 1, mt: '1px' }} />}
        </Box>
        {onViewDetails && (
          <Button variant="outlined" size="small" onClick={onViewDetails} sx={{ ml: 2, flexShrink: 0 }}>
            {t('common:view_details')}
          </Button>
        )}
      </Stack>

      <Stack
        mt={1}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={1}
        style={{ color: newItem ? bgColor : defaultTimelineItemBGColor }}
      >
        <Stack direction="column" spacing={0.5}>
          {item.tagGroup && (
            <Chip
              label={item.tagGroup.name}
              style={{ marginBottom: 0, marginRight: '4px', color: 'white', backgroundColor: item.tagGroup ? item.tagGroup.colorHex : bgColor }}
              size="small"
            />
          )}
          {/* TODO: Remove internal user check when we want to release reskins to all users */}
          {archetypeName && internalUser && <Chip label={archetypeName} variant="outlined" size="small" />}
        </Stack>
        {item.performanceChanges && item.end_time < Date.now() && item.trackedGame?.game?.isMobileGame() && (
          <EventKPIIndicator
            revenueChange={item.performanceChanges.REVENUE_DAY_1_CHANGE}
            downloadChange={item.performanceChanges.DOWNLOAD_DAY_1_CHANGE}
            dauChange={item.performanceChanges.DAU_DAY_1_CHANGE}
            startTime={item.start_time}
            endTime={item.end_time}
            hideYear
            hideStatusChip
            showComparisonRange
            showAllKpiChips
          />
        )}
      </Stack>

      <Grid container spacing={2} style={{ color: newItem ? bgColor : defaultTimelineItemBGColor }} maxWidth={600}>
        <Grid item xs>
          {showWipMessage ? (
            <Typography variant="body2" sx={{ mt: 1.5, mb: 1.5, fontStyle: 'italic' }}>
              {t('live-events:event_wip_tooltip')}
            </Typography>
          ) : (
            descriptionRawHtml && (
              <Box mt={1.5} mb={1.5} sx={{ '& *': { fontSize: '14px !important' } }}>
                <HtmlContentParser rawHtml={descriptionRawHtml} parserOptions={liveEventReviewModalParserOptions(item.trackingEvent.gameId || '')} />
              </Box>
            )
          )}

          <PriceRangeIndicator priceRange={priceRange} sx={{ mb: 1 }} />

          {displayExperimentalFeatureMotivations && item.trackingEvent.motivations?.motivations && item.trackingEvent.motivations?.motivations.length > 0 && (
            <Box className="LiveEventsCalendar__ItemMotivations" style={{ marginBottom: '-8px' }}>
              <Divider sx={{ mt: 2, mb: 1 }}>{t('motivations:motivations')}</Divider>
              {item.trackingEvent.motivations?.motivations.map((motivation) => {
                return <MotivationTypeBadge key={motivation.key} type={motivation.key} description={motivation.description} alignLeft />
              })}
            </Box>
          )}
        </Grid>

        {item.trackingEvent.screenshotUrl && (
          <Grid item alignSelf="flex-start">
            <AnimatedImage
              className="LiveEventsCalendar__ItemScreenshot"
              url={item.trackingEvent.screenshotUrl}
              alt="Screenshot"
              imgStyle={{ maxWidth: '200px', maxHeight: '200px' }}
              autoPlay
            />
          </Grid>
        )}
      </Grid>

      {blueprintDescription &&
        blueprintDescription.trim().length > 0 &&
        internalUser && ( // TODO: Remove internal user check when we want to release mechanic changes to all users
          <>
            <Box textAlign={'center'} sx={{ mt: 2 }}>
              <Divider style={{ margin: '10px 0px 10px' }}>{t('live-events:change_in_this_version')}</Divider>
            </Box>

            <Typography variant="body2" component="div" sx={{ mt: 1, mb: 2, fontStyle: 'italic', textAlign: 'justify' }}>
              <HtmlContentParser rawHtml={blueprintDescription} parserOptions={liveEventReviewModalParserOptions(item.trackedGame?.game?.id || '')} />
            </Typography>
          </>
        )}

      {newItem && (
        <Box textAlign={'center'}>
          <Divider style={{ margin: '10px 0px 10px' }} />
          <strong>
            <span style={{ fontSize: '13px' }}>{t('live-events:event_type_first_appearance')}</span>
          </strong>
        </Box>
      )}

      {firstInstance && (
        <Box textAlign={'center'}>
          <Divider style={{ margin: '10px 0px 10px' }} />
          <strong>
            <span style={{ fontSize: '13px' }}>{t('live-events:event_blueprint_first_instance_seen')}</span>
          </strong>
        </Box>
      )}

      {mechanicChange &&
        internalUser && ( // TODO: Remove internal user check when we want to release mechanic changes to all users
          <>
            <Box textAlign={'center'}>
              <Divider style={{ margin: '10px 0px 10px' }} />
              <strong>
                <span style={{ fontSize: '13px' }}>{t('live-events:event_mechanic_change')}</span>
              </strong>
            </Box>
          </>
        )}
    </Box>
  )
}
